import { useSyncExternalStore } from 'react'

/**
 * Recently viewed pages and records (E-UX), per user, per browser. Stored in localStorage under
 * the signed-in user's id and wiped on sign-out and on session expiry (spec 21 §3.3.4: the next
 * person on a shared machine must not see the previous user's employee names). Favourites are a
 * server-side preference instead (they follow the user; see lib/preferences.tsx).
 */
export interface PersonalLink { path: string; title: string; at: number }

const PREFIX = 'bigroi.personal.'
const MAX_RECENT = 8
const EVENT = 'bigroi:personal-changed'

let currentUser: string | null = null
/** Called by the shell once the identity is known. */
export function setPersonalUser(id: string | null) {
  if (currentUser === id) return
  currentUser = id
  emit()
}

function key(kind: 'recent') { return currentUser ? `${PREFIX}${kind}.${currentUser}` : null }
function read(kind: 'recent'): PersonalLink[] {
  const k = key(kind)
  if (!k) return EMPTY
  try {
    const raw = localStorage.getItem(k)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((x) => x && typeof x.path === 'string' && typeof x.title === 'string') : EMPTY
  } catch { return EMPTY }
}
function write(kind: 'recent', rows: PersonalLink[]) {
  const k = key(kind)
  if (!k) return
  try { localStorage.setItem(k, JSON.stringify(rows)) } catch { /* quota / private mode */ }
  emit()
}
const EMPTY: PersonalLink[] = []
let cache: Record<string, PersonalLink[]> = {}
function emit() { cache = {}; if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENT)) }
function snapshot(kind: 'recent'): PersonalLink[] {
  const k = `${kind}:${currentUser}`
  if (!cache[k]) cache[k] = read(kind)
  return cache[k]
}
function subscribe(cb: () => void) {
  const onStorage = (e: StorageEvent) => { if (e.key?.startsWith(PREFIX)) { cache = {}; cb() } }
  window.addEventListener(EVENT, cb)
  window.addEventListener('storage', onStorage)
  return () => { window.removeEventListener(EVENT, cb); window.removeEventListener('storage', onStorage) }
}

export function recordVisit(path: string, title: string) {
  if (!currentUser || !title || path === '/login') return
  const rows = read('recent').filter((r) => r.path !== path)
  write('recent', [{ path, title, at: Date.now() }, ...rows].slice(0, MAX_RECENT))
}

export function useRecents(): PersonalLink[] { return useSyncExternalStore(subscribe, () => snapshot('recent'), () => EMPTY) }

/** Wipe every user's recents in this browser (sign-out, session expiry). */
export function clearPersonalStores() {
  try {
    Object.keys(localStorage).filter((k) => k.startsWith(PREFIX)).forEach((k) => localStorage.removeItem(k))
  } catch { /* storage unavailable */ }
  currentUser = null
  emit()
}
